import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { pushWarehouseRecords, todayISODate } from '@/lib/warehouse-sync';
import { RefreshCw, Plus, Trash2, ShoppingBag, Target, Download, Check, Archive, Eye, LockKeyhole } from 'lucide-react';
import MultiSelect from '@/components/MultiSelect';
import { parseNumber, findColumnIndex } from '@/lib/channel-intel';
import { parseAnyDate } from '@/lib/date-utils';
import { formatInt, norm } from '@/lib/format-utils';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHANNELS = [
  { value: 'Amazon',           unitsCol: 'Amazon Units'     },
  { value: 'Flipkart',         unitsCol: 'Flipkart Units'   },
  { value: 'W.in',             unitsCol: 'W.in Units'       },
  { value: 'Offline',          unitsCol: 'Offline Units'    },
  { value: 'Blinkit',          unitsCol: 'Blinkit Units'    },
  { value: 'Zepto',            unitsCol: 'Zepto Units'      },
  { value: 'Meesho',           unitsCol: 'Meesho Units'     },
  { value: 'FK Mins',          unitsCol: 'Fk Mins Units'    },
  { value: 'AZ Now',           unitsCol: 'Amazon Now Units' },
];
const ALL = 'all';
// Default channel projection GROWTH (in %). `20` means +20% vs the base value
// (multiplier 1.20). Negative values support de-growth (e.g. -10 = ×0.90).
//   projected = baseValue × (1 + growthPct / 100)
const DEFAULT_PCT = 20;
// 3-letter lowercase tag used in formulas (e.g. `apr.Amazon`, `may.Blinkit`).
const monthTag = (m) => MONTHS[m].slice(0, 3).toLowerCase();

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const monthLabel = (m, y) => `${MONTHS[m].slice(0, 3)} ${y}`;

const previousMonths = (m, y) => {
  const out = [];
  for (let k = 2; k >= 1; k -= 1) {
    let mi = m - k;
    let yi = y;
    while (mi < 0) { mi += 12; yi -= 1; }
    out.push({ month: mi, year: yi });
  }
  return out;
};

// `n` consecutive months starting at (m, y), inclusive: [selected, +1, +2, …].
const futureMonths = (m, y, n) => {
  const out = [];
  for (let k = 0; k < n; k += 1) {
    let mi = m + k;
    let yi = y;
    while (mi > 11) { mi -= 12; yi += 1; }
    out.push({ month: mi, year: yi });
  }
  return out;
};

const monthKey = (m) => `${m.year}-${m.month}`;

export default function MonthlyRollingProjectionsPage() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth());
  const [year, setYear] = useState(now.getFullYear());

  const [projectionRows, setProjectionRows] = useState([]);
  const [mtdSalesRows, setMtdSalesRows] = useState([]);
  const [addedSkus, setAddedSkus] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedBrands, setSelectedBrands] = useState([]);
  const [channelFilter, setChannelFilter] = useState(ALL);

  // Edited projection cells across ALL 3 forward projection months. Outer key is
  // "year-month", inner key is "<sku_lc>__<channel>" → raw input string.
  const [editsByMonth, setEditsByMonth] = useState({});

  // Per-channel projection % multiplier (default 120) per projection month.
  // Outer key "year-month", inner key `${channel}` → number.
  const [pctByMonth, setPctByMonth] = useState({});

  // "Projection Updated" flags per (target month, channel). Outer key "year-month".
  const [updatedByMonth, setUpdatedByMonth] = useState({});

  // Which projection cell currently has focus (for formula editing). The shape is
  // `{ rowIdx, colIdx, projMonthKey, channel }` — colIdx maps to A1 grid columns.
  const [focusedCell, setFocusedCell] = useState(null);

  // Ref to the focused <input> — used to programmatically insert A1 refs at the
  // cursor position when the user clicks another cell during formula editing.
  const focusedInputRef = useRef(null);

  // Add SKU dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newAsin, setNewAsin] = useState('');
  const [newBrand, setNewBrand] = useState('');
  const [newSku, setNewSku] = useState('');
  const [saving, setSaving] = useState(false);

  // Pending debounced saves keyed by cell id.
  const saveTimers = useRef({});

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('radar_session_token')}`,
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    Expires: '0',
  });

  const fetchSheets = async () => {
    setLoading(true);
    setProjectionRows([]);
    setMtdSalesRows([]);
    try {
      const ts = Date.now();
      const cfg = { headers: authHeaders(), params: { _t: ts } };
      const [proj, mtd, added] = await Promise.all([
        axios.get(`${API}/sheets/projection`, cfg),
        axios.get(`${API}/sheets/mtd-sales`, cfg),
        axios.get(`${API}/rolling-projections/added-skus`, cfg),
      ]);
      setProjectionRows(proj.data.rows || []);
      setMtdSalesRows(mtd.data.rows || []);
      setAddedSkus(added.data.items || []);
      toast.success('Data refreshed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Pull stored edits, channel-% multipliers, AND "Projection Updated" flags
  // for ALL 3 projection months in parallel.
  const fetchEditsAndPct = async (y, m) => {
    try {
      const projMs = futureMonths(m, y, 3);
      const reqs = projMs.flatMap(pm => ([
        axios.get(`${API}/rolling-projections/edits`, {
          headers: authHeaders(),
          params: { year: pm.year, month: pm.month, _t: Date.now() },
        }),
        axios.get(`${API}/rolling-projections/channel-pct`, {
          headers: authHeaders(),
          params: { year: pm.year, month: pm.month, _t: Date.now() },
        }),
        axios.get(`${API}/rolling-projections/updated-flags`, {
          headers: authHeaders(),
          params: { year: pm.year, month: pm.month, _t: Date.now() },
        }),
      ]));
      const results = await Promise.all(reqs);
      const eByM = {};
      const pByM = {};
      const uByM = {};
      projMs.forEach((pm, i) => {
        const editsRes = results[i * 3];
        const pctRes = results[i * 3 + 1];
        const updRes = results[i * 3 + 2];
        const mk = monthKey(pm);
        const eMap = {};
        (editsRes.data.items || []).forEach(item => {
          eMap[`${norm(item.sku_name)}__${item.channel}`] = item.value == null ? '' : String(item.value);
        });
        eByM[mk] = eMap;
        const pMap = {};
        (pctRes.data.items || []).forEach(item => { pMap[item.channel] = Number(item.pct); });
        pByM[mk] = pMap;
        const uMap = {};
        (updRes.data.items || []).forEach(item => { uMap[item.channel] = !!item.updated; });
        uByM[mk] = uMap;
      });
      setEditsByMonth(eByM);
      setPctByMonth(pByM);
      setUpdatedByMonth(uByM);
    } catch (e) {
      console.warn('[rolling-projections] failed to load edits/pct/flags', e);
      setEditsByMonth({});
      setPctByMonth({});
      setUpdatedByMonth({});
    }
  };

  useEffect(() => { fetchSheets(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { fetchEditsAndPct(year, month); /* eslint-disable-next-line */ }, [year, month]);

  const baseRows = useMemo(() => {
    const out = [];
    if (projectionRows && projectionRows.length > 1) {
      const hdr = projectionRows[0];
      const asinIdx = findColumnIndex(hdr, ['asin']);
      const brandIdx = findColumnIndex(hdr, ['brand']);
      const skuIdx = findColumnIndex(hdr, ['sku name', 'sku', 'product name']);
      projectionRows.slice(1).forEach(r => {
        const sku = (r[skuIdx] || '').toString().trim();
        if (!sku) return;
        out.push({
          asin: asinIdx !== -1 ? (r[asinIdx] || '').toString().trim() : '',
          brand: brandIdx !== -1 ? (r[brandIdx] || '').toString().trim() : '',
          sku,
          source: 'sheet',
        });
      });
    }
    addedSkus.forEach(item => {
      out.push({
        asin: item.asin || '',
        brand: item.brand || '',
        sku: item.sku_name || '',
        source: 'custom',
        id: item.id,
      });
    });
    return out;
  }, [projectionRows, addedSkus]);

  const months = useMemo(() => previousMonths(month, year), [month, year]);
  // Newer of the two months shown — used as the 120% base for projections.
  const baseMonth = months[1];

  const salesByMonthSku = useMemo(() => {
    const out = {};
    if (!mtdSalesRows || mtdSalesRows.length < 2) return out;
    const hdr = mtdSalesRows[0];
    const dateIdx = findColumnIndex(hdr, ['date']);
    const skuIdxs = [
      findColumnIndex(hdr, ['sku 1']),
      findColumnIndex(hdr, ['sku 2']),
      findColumnIndex(hdr, ['sku 3']),
      findColumnIndex(hdr, ['sku 4']),
    ].filter(i => i !== -1);
    if (dateIdx === -1 || skuIdxs.length === 0) return out;

    const chanColIdx = {};
    CHANNELS.forEach(c => {
      const idx = findColumnIndex(hdr, [c.unitsCol]);
      if (idx !== -1) chanColIdx[c.value] = idx;
    });

    const targetKeys = new Set(months.map(m => `${m.year}-${m.month}`));

    mtdSalesRows.slice(1).forEach(row => {
      const d = parseAnyDate(row[dateIdx]);
      if (!d) return;
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      if (!targetKeys.has(key)) return;

      const skus = [];
      const seen = new Set();
      skuIdxs.forEach(si => {
        const v = (row[si] || '').toString().trim();
        if (!v) return;
        const k = norm(v);
        if (seen.has(k)) return;
        seen.add(k);
        skus.push(v);
      });
      if (skus.length === 0) return;

      skus.forEach(skuName => {
        const k = norm(skuName);
        if (!out[k]) out[k] = {};
        if (!out[k][key]) out[k][key] = {};
        Object.entries(chanColIdx).forEach(([ch, ci]) => {
          out[k][key][ch] = (out[k][key][ch] || 0) + parseNumber(row[ci]);
        });
      });
    });
    return out;
  }, [mtdSalesRows, months]);

  // Latest non-zero sales DAY of month per (sku, channel) within the BASE month.
  // Used to project a full-month run-rate: (currentSales / latestDay) * daysInBaseMonth.
  // Shape: { [skuNormKey]: { [channel]: dayNumber (1..31) } }.
  const baseMonthLatestDay = useMemo(() => {
    const out = {};
    if (!mtdSalesRows || mtdSalesRows.length < 2) return out;
    const hdr = mtdSalesRows[0];
    const dateIdx = findColumnIndex(hdr, ['date']);
    const skuIdxs = [
      findColumnIndex(hdr, ['sku 1']),
      findColumnIndex(hdr, ['sku 2']),
      findColumnIndex(hdr, ['sku 3']),
      findColumnIndex(hdr, ['sku 4']),
    ].filter(i => i !== -1);
    if (dateIdx === -1 || skuIdxs.length === 0) return out;
    const chanColIdx = {};
    CHANNELS.forEach(c => {
      const idx = findColumnIndex(hdr, [c.unitsCol]);
      if (idx !== -1) chanColIdx[c.value] = idx;
    });
    mtdSalesRows.slice(1).forEach(row => {
      const d = parseAnyDate(row[dateIdx]);
      if (!d) return;
      if (d.getFullYear() !== baseMonth.year || d.getMonth() !== baseMonth.month) return;
      const day = d.getDate();
      // Same SKU collection rule.
      const skus = [];
      const seen = new Set();
      skuIdxs.forEach(si => {
        const v = (row[si] || '').toString().trim();
        if (!v) return;
        const k = norm(v);
        if (seen.has(k)) return;
        seen.add(k);
        skus.push(v);
      });
      if (skus.length === 0) return;
      skus.forEach(skuName => {
        const k = norm(skuName);
        if (!out[k]) out[k] = {};
        Object.entries(chanColIdx).forEach(([ch, ci]) => {
          if (parseNumber(row[ci]) > 0) {
            if (!out[k][ch] || out[k][ch] < day) out[k][ch] = day;
          }
        });
      });
    });
    return out;
  }, [mtdSalesRows, baseMonth]);

  const brandOptions = useMemo(() => {
    const set = new Set();
    baseRows.forEach(r => { if (r.brand) set.add(r.brand); });
    return [...set].sort().map(b => ({ value: b, label: b }));
  }, [baseRows]);

  const visibleChannels = useMemo(
    () => (channelFilter === ALL ? CHANNELS : CHANNELS.filter(c => c.value === channelFilter)),
    [channelFilter],
  );

  const filteredRows = useMemo(() => {
    if (selectedBrands.length === 0) return baseRows;
    return baseRows.filter(r => selectedBrands.includes(r.brand));
  }, [baseRows, selectedBrands]);

  // 3 forward projection months (selected + 2 future). Declared here so `gridCells`
  // and the projection-evaluation helpers below can reference it without TDZ issues.
  const projectionMonths = useMemo(() => futureMonths(month, year, 3), [month, year]);

  const lookupSalesRawPrime = (skuName, mKey, channel) =>
    salesByMonthSku[norm(skuName)]?.[mKey]?.[channel] || 0;

  // Channel multiplier for a target projection month (returns DEFAULT_PCT GROWTH%
  // when no override). The value is a growth %, e.g. 20 = +20% (×1.20),
  // -10 = -10% (×0.90).
  const pctFor = (target, channel) => {
    const v = pctByMonth[monthKey(target)]?.[channel];
    return v != null ? v : DEFAULT_PCT;
  };

  // Days in a calendar month.
  const daysIn = (m) => new Date(m.year, m.month + 1, 0).getDate();

  // PROJECTED FULL-MONTH UNITS — applied ONLY to the immediate previous month
  // (i.e. `baseMonth` = months[1]). Older months (Apr in a Jun-selected view) keep
  // their actual partial-period totals.
  //
  // Formula:
  //   projectedFull = (currentSales / latestNonZeroDay) × daysInMonth
  //
  // Falls back to the raw actual when no non-zero days were recorded (we have
  // nothing to extrapolate from).
  const projectedBaseFull = (skuName, channel) => {
    const baseKey = monthKey(baseMonth);
    const currentSales = lookupSalesRaw(skuName, baseKey, channel);
    const latestDay = baseMonthLatestDay[norm(skuName)]?.[channel];
    if (!latestDay || latestDay <= 0) return currentSales;
    return Math.round((currentSales / latestDay) * daysIn(baseMonth));
  };

  // Display-layer sales lookup:
  //   • base month  → projected full-month equivalent (per spec).
  //   • other month → raw actual sales.
  const lookupSales = (skuName, mKey, channel) => {
    if (mKey === monthKey(baseMonth)) return projectedBaseFull(skuName, channel);
    return lookupSalesRaw(skuName, mKey, channel);
  };

  // Raw (un-projected) actual sales — used by autoProjection and by the base-month
  // projector itself to avoid recursion.
  function lookupSalesRaw(skuName, mKey, channel) {
    return lookupSalesRawPrime(skuName, mKey, channel);
  }

  // Auto projection for a (target month, sku, channel):
  //   autoProj = projectedBaseFull × (1 + growthPct / 100)
  // The same projected May value drives every projection month (Jun/Jul/Aug),
  // varied only by each month's channel-%.
  const autoProjection = (target, skuName, channel) => {
    const baseFull = projectedBaseFull(skuName, channel);
    const pct = pctFor(target, channel) / 100;
    return Math.round(baseFull * (1 + pct));
  };

  // Formula context — `may.Amazon` etc. resolve to the PROJECTED full-month value
  // for the base month, actuals for older months. Bare channel = base month
  // (projected). A1-style cell refs (`A2`, `B5`) are resolved separately by
  // evalFormula via the visible grid cache.
  const formulaCtx = (skuName) => {
    const ctx = {};
    months.forEach(m => {
      const tag = monthTag(m.month);
      const mKey = monthKey(m);
      CHANNELS.forEach(c => {
        ctx[`${tag}.${c.value.toLowerCase()}`] = lookupSales(skuName, mKey, c.value);
      });
    });
    const baseTag = monthTag(baseMonth.month);
    CHANNELS.forEach(c => {
      const lc = c.value.toLowerCase();
      ctx[lc] = ctx[`${baseTag}.${lc}`] || 0;
    });
    return ctx;
  };

  // ============ Excel-style A1 cell-reference grid ============
  //
  // Each visible NUMERIC column is assigned a letter (A, B, C, …, Z, AA, AB, …)
  // in display order. Data rows are 1-indexed (1 = first data row).
  //
  //   Column layout (with default 9 visible channels per group):
  //     A..I  → Apr Channel[0..8]
  //     J..R  → May Channel[0..8]   (note: May = base month, returns PROJECTED full-month value)
  //     S..AA → Proj month #1 (projected cell value)
  //     AB..AJ → Proj month #2
  //     AK..AS → Proj month #3
  //
  // Filter changes (e.g. channel chip) shift which channels are visible — the
  // letter mapping always follows the current visible layout, mirroring Excel.
  const colLetters = (idx0) => {
    let n = idx0 + 1;
    let out = '';
    while (n > 0) {
      const rem = (n - 1) % 26;
      out = String.fromCharCode(65 + rem) + out;
      n = Math.floor((n - 1) / 26);
    }
    return out;
  };
  const lettersToIdx = (letters) => {
    let n = 0;
    for (let i = 0; i < letters.length; i += 1) {
      n = n * 26 + (letters.charCodeAt(i) - 64);
    }
    return n - 1;
  };

  // Map a logical numeric-column letter back to (groupType, channel) — used to
  // resolve `A1`-style refs on-demand without materialising a grid (would create
  // a circular dep with `projectionValue`).
  const colMeta = useMemo(() => {
    const meta = [];
    months.forEach((m) => visibleChannels.forEach((c) => meta.push({
      kind: 'sales', month: m, channel: c.value,
    })));
    projectionMonths.forEach((pm) => visibleChannels.forEach((c) => meta.push({
      kind: 'proj', month: pm, channel: c.value,
    })));
    return meta;
  }, [months, projectionMonths, visibleChannels]);

  // Resolve `A1`-style refs lazily.
  // colIdx → (month, channel); rowIdx → filteredRows[rowIdx].sku.
  // Out-of-range refs return 0 so partial formulas don't blow up.
  const a1RefValue = (refStr) => {
    const m = refStr.match(/^\$?([A-Z]+)\$?(\d+)$/);
    if (!m) return null;
    const col = lettersToIdx(m[1]);
    const row = parseInt(m[2], 10) - 1;
    const cm = colMeta[col];
    const r = filteredRows[row];
    if (!cm || !r) return 0;
    if (cm.kind === 'sales') return lookupSales(r.sku, monthKey(cm.month), cm.channel);
    return projectionValueRaw(cm.month, r.sku, cm.channel);
  };

  // Resolve the projected value WITHOUT eval — used by a1RefValue to break the
  // cycle (projectionValue → evalFormula → a1RefValue → projectionValue). When a
  // referenced cell is itself a formula, we return the AUTO projection instead
  // of re-evaluating (a one-hop lookup; full multi-hop deps would need a real
  // dependency graph, which is overkill for this use case).
  const projectionValueRaw = (target, skuName, channel) => {
    const ek = `${norm(skuName)}__${channel}`;
    const raw = editsByMonth[monthKey(target)]?.[ek];
    if (raw == null || raw === '') return autoProjection(target, skuName, channel);
    if (typeof raw === 'string' && raw.trim().startsWith('=')) {
      return autoProjection(target, skuName, channel); // skip eval to avoid recursion
    }
    const num = Number((raw || '').toString().replace(/,/g, ''));
    return Number.isFinite(num) ? num : autoProjection(target, skuName, channel);
  };

  const evalFormula = (text, skuName) => {
    if (typeof text !== 'string' || !text.trim().startsWith('=')) {
      return { value: NaN, ok: false };
    }
    const ctx = formulaCtx(skuName);
    let expr = text.trim().slice(1);

    // 1) Resolve A1-style cell refs FIRST (longest patterns first to avoid
    //    eating part of identifiers like "may.Amazon").
    expr = expr.replace(/\$?([A-Z]+)\$?(\d+)/g, (m) => {
      const v = a1RefValue(m);
      return v == null ? m : String(v);
    });

    // 2) Resolve channel-name identifiers (`may.Amazon`, `Flipkart`, ...).
    expr = expr.replace(/[a-z][a-z]*(?:\.[a-z][a-z]*)?(?:\s*[A-Za-z. ]*)?/gi, (m) => {
      const key = m.trim().toLowerCase().replace(/\s+/g, '');
      if (key in ctx) return String(ctx[key]);
      const aliased = key.replace(/^fkmins$/, 'fk mins').replace(/^aznow$/, 'az now');
      if (aliased in ctx) return String(ctx[aliased]);
      return m;
    });

    if (!/^[\d+\-*/().\s]*$/.test(expr)) return { value: NaN, ok: false };
    try {
      // eslint-disable-next-line no-new-func
      const v = Function(`"use strict"; return (${expr});`)();
      if (typeof v !== 'number' || !isFinite(v)) return { value: NaN, ok: false };
      return { value: v, ok: true };
    } catch {
      return { value: NaN, ok: false };
    }
  };

  // Shift every A1 ref inside a formula string by (rowDelta, colDelta) — used
  // when pasting a copied formula into another cell (Excel-style relative refs).
  // Absolute refs `$A$2` are NOT shifted (standard Excel semantics).
  const shiftFormulaRefs = (formula, rowDelta, colDelta) => formula.replace(
    /(\$?)([A-Z]+)(\$?)(\d+)/g,
    (m, colAbs, letters, rowAbs, rowDigits) => {
      const newCol = colAbs === '$' ? letters : colLetters(Math.max(0, lettersToIdx(letters) + colDelta));
      const newRow = rowAbs === '$' ? rowDigits : String(Math.max(1, parseInt(rowDigits, 10) + rowDelta));
      return `${colAbs}${newCol}${rowAbs}${newRow}`;
    },
  );

  // Effective projection value for a given (target month, sku, channel).
  const projectionValue = (target, skuName, channel) => {
    const ek = `${norm(skuName)}__${channel}`;
    const raw = editsByMonth[monthKey(target)]?.[ek];
    if (raw == null || raw === '') return autoProjection(target, skuName, channel);
    if (typeof raw === 'string' && raw.trim().startsWith('=')) {
      const { value, ok } = evalFormula(raw, skuName);
      return ok ? Math.round(value) : autoProjection(target, skuName, channel);
    }
    const num = Number((raw || '').toString().replace(/,/g, ''));
    return Number.isFinite(num) ? num : autoProjection(target, skuName, channel);
  };

  // 3 forward projection months (selected + 2 future).
  // (Moved earlier in the file so memos below can use it.)

  // Totals & summary cards.
  // Last-2-months total = sum of all sales cells visible under both prior-month groups
  // (respects brand + channel filters).
  const totals = useMemo(() => {
    const out = {};
    months.forEach(m => {
      const mKey = monthKey(m);
      visibleChannels.forEach(c => {
        const sum = filteredRows.reduce(
          (s, r) => s + lookupSales(r.sku, mKey, c.value), 0,
        );
        out[`${mKey}__${c.value}`] = sum;
      });
    });
    // Projection totals per (target month, channel)
    projectionMonths.forEach(pm => {
      const pKey = monthKey(pm);
      visibleChannels.forEach(c => {
        const sum = filteredRows.reduce(
          (s, r) => s + projectionValue(pm, r.sku, c.value), 0,
        );
        out[`proj-${pKey}__${c.value}`] = sum;
      });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRows, months, projectionMonths, visibleChannels, salesByMonthSku, editsByMonth, pctByMonth]);

  const totalLast2MonthsSales = useMemo(() => {
    let sum = 0;
    months.forEach(m => {
      const mKey = monthKey(m);
      visibleChannels.forEach(c => { sum += totals[`${mKey}__${c.value}`] || 0; });
    });
    return sum;
  }, [totals, months, visibleChannels]);

  const perMonthSalesTotal = useMemo(() => {
    const out = {};
    months.forEach(m => {
      const mKey = monthKey(m);
      let sum = 0;
      visibleChannels.forEach(c => { sum += totals[`${mKey}__${c.value}`] || 0; });
      out[mKey] = sum;
    });
    return out;
  }, [totals, months, visibleChannels]);

  // Total projected across ALL 3 projection months (visible channels).
  const totalProjected = useMemo(() => {
    let sum = 0;
    projectionMonths.forEach(pm => {
      const pKey = monthKey(pm);
      visibleChannels.forEach(c => { sum += totals[`proj-${pKey}__${c.value}`] || 0; });
    });
    return sum;
  }, [totals, projectionMonths, visibleChannels]);

  // Per-projection-month totals (for the card's 3-month split).
  const perMonthProjectionTotal = useMemo(() => {
    const out = {};
    projectionMonths.forEach(pm => {
      const pKey = monthKey(pm);
      let sum = 0;
      visibleChannels.forEach(c => { sum += totals[`proj-${pKey}__${c.value}`] || 0; });
      out[pKey] = sum;
    });
    return out;
  }, [totals, projectionMonths, visibleChannels]);

  // Diff-based tone for edited projection cells (vs the auto value) per target month.
  const editTone = (target, skuName, channel) => {
    const ek = `${norm(skuName)}__${channel}`;
    const raw = editsByMonth[monthKey(target)]?.[ek];
    if (raw == null || raw === '') {
      return { cell: 'bg-emerald-50/40', input: 'border-transparent hover:border-emerald-300 focus:border-emerald-500' };
    }
    if (typeof raw === 'string' && raw.trim().startsWith('=')) {
      return { cell: 'bg-sky-100', input: 'border-sky-400 text-sky-900 font-medium' };
    }
    const auto = autoProjection(target, skuName, channel);
    const num = Number((raw || '').toString().replace(/,/g, ''));
    const diff = Math.abs(num - auto);
    if (diff < 100) return { cell: 'bg-emerald-100', input: 'border-emerald-400 text-emerald-900 font-medium' };
    if (diff <= 1000) return { cell: 'bg-amber-100', input: 'border-amber-400 text-amber-900 font-medium' };
    return { cell: 'bg-red-100', input: 'border-red-400 text-red-900 font-semibold' };
  };

  // Toggle "Projection Updated" badge for a (target month, channel).
  const handleToggleUpdated = async (target, channel) => {
    const mk = monthKey(target);
    const next = !(updatedByMonth[mk]?.[channel]);
    setUpdatedByMonth(prev => {
      const cp = { ...prev };
      const inner = { ...(cp[mk] || {}) };
      if (next) inner[channel] = true; else delete inner[channel];
      cp[mk] = inner;
      return cp;
    });
    try {
      await axios.put(`${API}/rolling-projections/updated-flags`, {
        year: target.year, month: target.month, channel, updated: next,
      }, { headers: authHeaders() });
    } catch (e) {
      toast.error('Failed to save updated flag');
      fetchEditsAndPct(year, month);
    }
  };

  // Click-to-insert: if a projection cell is currently being edited and the user
  // clicks ANOTHER numeric cell, insert that cell's A1 ref into the focused
  // formula at the cursor (Excel-like). Returns true if the click was consumed.
  const insertA1Ref = (ref) => {
    const input = focusedInputRef.current;
    if (!input || !focusedCell) return false;
    const cur = input.value || '';
    // Only insert when we're inside an actively-typed formula (starts with "=").
    if (!cur.trim().startsWith('=')) return false;
    const start = input.selectionStart ?? cur.length;
    const end = input.selectionEnd ?? cur.length;
    const next = cur.slice(0, start) + ref + cur.slice(end);
    // Persist the change via the existing edit handler.
    handleProjectionEdit(
      focusedCell.target, focusedCell.skuName, focusedCell.channel, next,
    );
    // Move caret after the inserted ref.
    setTimeout(() => {
      input.focus();
      const pos = start + ref.length;
      input.setSelectionRange(pos, pos);
    }, 0);
    return true;
  };

  // Copy/paste with relative refs — uses module-local memory (not the system
  // clipboard) so multi-cell formulas survive a paste round-trip exactly.
  const handleCellKeyDown = (target, skuName, channel, rowIdx, colIdx, e) => {
    if (!(e.ctrlKey || e.metaKey)) return;
    if (e.key === 'c' || e.key === 'C') {
      const ek = `${norm(skuName)}__${channel}`;
      const raw = editsByMonth[monthKey(target)]?.[ek] || '';
      if (!raw) return;
      // Only intercept Ctrl+C when there's an actual formula to relativise —
      // numeric edits work via the browser's default copy on selected text.
      if (!String(raw).trim().startsWith('=')) return;
      window.__radarClip = { formula: String(raw), srcRow: rowIdx, srcCol: colIdx };
      e.preventDefault();
      toast.success('Formula copied');
    } else if (e.key === 'v' || e.key === 'V') {
      const clip = window.__radarClip;
      if (!clip) return;
      const shifted = shiftFormulaRefs(clip.formula, rowIdx - clip.srcRow, colIdx - clip.srcCol);
      e.preventDefault();
      handleProjectionEdit(target, skuName, channel, shifted);
      toast.success('Formula pasted (refs shifted)');
    }
  };

  // Persist projection cell edit (debounced 600ms per cell). Stores the RAW input
  // string. Edits live per (year, month, sku, channel) so cells across the 3
  // projection months never collide.
  const handleProjectionEdit = (target, skuName, channel, raw) => {
    const ek = `${norm(skuName)}__${channel}`;
    const trimmed = (raw == null ? '' : raw.toString()).trim();
    const mk = monthKey(target);
    setEditsByMonth(prev => {
      const cp = { ...prev };
      const inner = { ...(cp[mk] || {}) };
      if (trimmed === '') delete inner[ek]; else inner[ek] = trimmed;
      cp[mk] = inner;
      return cp;
    });
    schedulePersist(target, ek, skuName, channel, trimmed === '' ? null : trimmed);
  };

  const schedulePersist = (target, ek, skuName, channel, value) => {
    const timerKey = `${monthKey(target)}__${ek}`;
    if (saveTimers.current[timerKey]) clearTimeout(saveTimers.current[timerKey]);
    saveTimers.current[timerKey] = setTimeout(async () => {
      try {
        await axios.put(`${API}/rolling-projections/edits`, {
          year: target.year, month: target.month, sku_name: skuName, channel, value,
        }, { headers: authHeaders() });
      } catch (e) {
        toast.error('Failed to save projection edit');
        fetchEditsAndPct(year, month);
      } finally {
        delete saveTimers.current[timerKey];
      }
    }, 600);
  };

  // Change the projection % for a (target month, channel). Regenerates the column
  // for that target — clears all edits in that month for the channel.
  const handlePctChange = async (target, channel, rawValue) => {
    const num = rawValue === '' ? null : Number(rawValue);
    if (rawValue !== '' && (!Number.isFinite(num) || num < 0)) return;
    const mk = monthKey(target);
    setPctByMonth(prev => {
      const cp = { ...prev };
      const inner = { ...(cp[mk] || {}) };
      if (num == null) delete inner[channel]; else inner[channel] = num;
      cp[mk] = inner;
      return cp;
    });
    setEditsByMonth(prev => {
      const cp = { ...prev };
      const inner = { ...(cp[mk] || {}) };
      Object.keys(inner).forEach(k => { if (k.endsWith(`__${channel}`)) delete inner[k]; });
      cp[mk] = inner;
      return cp;
    });
    try {
      await axios.put(`${API}/rolling-projections/channel-pct`, {
        year: target.year, month: target.month, channel, pct: num,
      }, { headers: authHeaders() });
      await axios.delete(`${API}/rolling-projections/edits`, {
        headers: authHeaders(),
        params: { year: target.year, month: target.month, channel, _t: Date.now() },
      });
    } catch (e) {
      toast.error('Failed to save % override');
      fetchEditsAndPct(year, month);
    }
  };

  const handleAddSku = async () => {
    if (!newAsin.trim() || !newBrand.trim() || !newSku.trim()) {
      toast.error('ASIN, Brand and SKU NAME are all required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/rolling-projections/added-skus`,
        { asin: newAsin.trim(), brand: newBrand.trim(), sku_name: newSku.trim() },
        { headers: authHeaders() },
      );
      toast.success('SKU added');
      setDialogOpen(false);
      setNewAsin(''); setNewBrand(''); setNewSku('');
      const ts = Date.now();
      const added = await axios.get(`${API}/rolling-projections/added-skus`, {
        headers: authHeaders(), params: { _t: ts },
      });
      setAddedSkus(added.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add SKU');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSku = async (id) => {
    try {
      await axios.delete(`${API}/rolling-projections/added-skus/${id}`, {
        headers: authHeaders(),
      });
      toast.success('SKU removed');
      setAddedSkus(prev => prev.filter(x => x.id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete SKU');
    }
  };

  const yearOptions = useMemo(() => {
    const y0 = now.getFullYear();
    return [y0 - 2, y0 - 1, y0, y0 + 1, y0 + 2, y0 + 3];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build & download an XLSX of the currently visible table.
  // Captures: base cols + both month groups + projection group with current
  // channel filter, brand filter, month/year applied. Numbers are written as
  // numeric (not strings) so Excel can sum them downstream.
  //
  // Hardening notes:
  //   • Wrapped in try/catch so failures surface as a toast.error instead
  //     of dying silently in the console.
  //   • Sheet names are sanitized — Excel rejects names > 31 chars or
  //     containing : \ / ? * [ ]. Without this, `writeFile` throws and
  //     no file is produced.
  //   • Skip empty merges (where start==end) — xlsx 0.18 occasionally
  //     produces a corrupted file when a 1-cell merge is present, which
  //     happens whenever the channel filter is set to a single channel.
  //   • If filteredRows is empty we still emit a valid file with just the
  //     headers so the user gets feedback that the export ran.
  const sanitizeSheetName = (name) =>
    name.replace(/[:\\/?*[\]]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 31) || 'Sheet1';
  const handleDownloadExcel = () => {
    try {
      if (loading) {
        toast.error('Data is still loading — please wait a moment and try again');
        return;
      }
      if (!visibleChannels.length || !months.length || projectionMonths.length < 3) {
        toast.error('Cannot export: data is not ready yet');
        return;
      }
      if (filteredRows.length === 0) {
        toast.error('No rows match the current filters — clear filters and try again');
        return;
      }
      const baseCols = ['ASIN', 'Brand', 'SKU Name'];
      const groupRow = [...baseCols.map(() => '')];
      months.forEach(m => {
        groupRow.push(monthLabel(m.month, m.year));
        for (let i = 1; i < visibleChannels.length; i += 1) groupRow.push('');
      });
      projectionMonths.forEach(pm => {
        groupRow.push(`${monthLabel(pm.month, pm.year)} Auto Projection`);
        for (let i = 1; i < visibleChannels.length; i += 1) groupRow.push('');
      });

      const subRow = [...baseCols];
      months.forEach(() => visibleChannels.forEach(c => subRow.push(c.value)));
      projectionMonths.forEach(() => visibleChannels.forEach(c => subRow.push(c.value)));

      const aoa = [groupRow, subRow];

      filteredRows.forEach(r => {
        const row = [r.asin || '', r.brand || '', r.sku];
        months.forEach(m => {
          const mKey = monthKey(m);
          visibleChannels.forEach(c => row.push(lookupSales(r.sku, mKey, c.value)));
        });
        projectionMonths.forEach(pm => {
          visibleChannels.forEach(c => row.push(projectionValue(pm, r.sku, c.value)));
        });
        aoa.push(row);
      });

      const totalsRow = ['Total', '', ''];
      months.forEach(m => {
        const mKey = monthKey(m);
        visibleChannels.forEach(c => totalsRow.push(totals[`${mKey}__${c.value}`] || 0));
      });
      projectionMonths.forEach(pm => {
        const pKey = monthKey(pm);
        visibleChannels.forEach(c => totalsRow.push(totals[`proj-${pKey}__${c.value}`] || 0));
      });
      aoa.push(totalsRow);

      const ws = XLSX.utils.aoa_to_sheet(aoa);

      // Merges — skip any single-cell range; some Excel/xlsx versions
      // produce corrupted files for {s==e} merges (manifests as a silent
      // failure when channelFilter is a single channel).
      const merges = [];
      const colsPerGroup = visibleChannels.length;
      for (let c = 0; c < 3; c += 1) {
        if (1 > 0) merges.push({ s: { r: 0, c }, e: { r: 1, c } }); // 2-row merge on base cols — always safe
      }
      let cursor = 3;
      const pushGroupMerge = () => {
        if (colsPerGroup > 1) {
          merges.push({ s: { r: 0, c: cursor }, e: { r: 0, c: cursor + colsPerGroup - 1 } });
        }
        cursor += colsPerGroup;
      };
      months.forEach(pushGroupMerge);
      projectionMonths.forEach(pushGroupMerge);
      ws['!merges'] = merges;

      const totalGroups = months.length + projectionMonths.length;
      ws['!cols'] = [
        { wch: 14 }, { wch: 14 }, { wch: 36 },
        ...Array(totalGroups * colsPerGroup).fill({ wch: 12 }),
      ];

      const wb = XLSX.utils.book_new();
      const sheetName = sanitizeSheetName(
        `${monthLabel(month, year)}-${monthLabel(projectionMonths[2].month, projectionMonths[2].year)}`
      );
      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      const channelTag = channelFilter === ALL ? 'AllChannels' : channelFilter.replace(/[^a-z0-9]/gi, '');
      const brandTag = selectedBrands.length === 0 ? 'AllBrands' : `${selectedBrands.length}Brand`;
      const fileName = `RollingProjections_${monthLabel(month, year).replace(/\s+/g, '')}-${monthLabel(projectionMonths[2].month, projectionMonths[2].year).replace(/\s+/g, '')}_${channelTag}_${brandTag}.xlsx`;

      XLSX.writeFile(wb, fileName);
      toast.success(`Excel downloaded · ${filteredRows.length} SKU${filteredRows.length === 1 ? '' : 's'} · ${fileName}`);
    } catch (err) {
      // Surface the actual error to the user instead of failing silently.
      // Most often: invalid sheet name, blocked download (browser policy),
      // or a transient memory issue when the table is huge.
      console.error('[rolling-projections] Excel export failed', err);
      toast.error(`Excel export failed: ${err?.message || 'unknown error'}`);
    }
  };

  // ============ Historical Snapshots ============
  // Capture the visible grid as a frozen MongoDB document. Once saved, snapshots
  // are read from the API only (never reconciled with Google Sheets), so users
  // can audit projections from any past month even after the live sheet moves
  // on. Duplicate detection lives server-side — a SHA-256 of canonical rows
  // (per projection_month + projection_year) — so re-clicking Save on the same
  // data won't grow the collection. Genuine changes increment `version`
  // (v1, v2, v3 …) within that (month, year) bucket.
  const [snapshotsList, setSnapshotsList] = useState([]);
  const [savingSnapshot, setSavingSnapshot] = useState(false);
  const [snapshotsDialogOpen, setSnapshotsDialogOpen] = useState(false);
  const [viewingSnapshot, setViewingSnapshot] = useState(null);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  // Auto-Save: when on, edits to projection values, growth %, or "Projection
  // Updated" flags debounce-trigger a Save. Backend dedup ensures no-op cycles
  // (e.g. type-undo-type) don't pollute the version log.
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(
    () => localStorage.getItem('rolling_autosave') === '1'
  );
  useEffect(() => {
    localStorage.setItem('rolling_autosave', autoSaveEnabled ? '1' : '0');
  }, [autoSaveEnabled]);
  // Admin delete flow re-uses the editor password unlock established for the
  // Allocation Planner. Kept session-scoped so the user doesn't have to retype
  // between deletes; password is sent on each DELETE as `X-Editor-Password`.
  const [adminUnlocked, setAdminUnlocked] = useState(
    () => sessionStorage.getItem('editor_unlocked') === '1'
  );
  const [adminPwInMem, setAdminPwInMem] = useState(
    () => sessionStorage.getItem('editor_pw') || ''
  );
  const [adminPwDialogOpen, setAdminPwDialogOpen] = useState(false);
  const [adminPwInput, setAdminPwInput] = useState('');
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  // Build the exact-as-visible payload that the backend will hash and store.
  // Shape per row:
  //   { asin, brand, sku,
  //     history:    [{ month, year, monthName, channels: {<ch>:units} }, …],     ← 2 actual months
  //     projections:[{ month, year, monthName, channels: {<ch>:units},
  //                    growthPctByChannel: {<ch>:pct} }, …]                       ← 3 forward months
  //   }
  // Channels included are limited to `visibleChannels` so the snapshot mirrors
  // the on-screen state (channel filter respected).
  // ============ Auto-sync to MongoDB historical warehouse ============
  // Fires once whenever the grid data finishes loading (filteredRows becomes
  // non-empty). Builds one record per visible SKU × visible channel using the
  // SAME helpers (`projectionValue`, `lookupSales`) that paint the UI, so the
  // warehouse mirrors what the user actually saw. Dispatched is omitted here
  // (Rolling Projections has no dispatched stream) — backend won't overwrite
  // any dispatched value the Consumption page already wrote.
  const warehouseSyncedKeyRef = useRef(null);
  useEffect(() => {
    if (loading || filteredRows.length === 0 || visibleChannels.length === 0) return;
    const key = `${todayISODate()}|${month}-${year}|${filteredRows.length}|${visibleChannels.length}`;
    if (warehouseSyncedKeyRef.current === key) return;
    warehouseSyncedKeyRef.current = key;

    const today = todayISODate();
    const freshestProj = projectionMonths[0];
    const latestHist   = months[months.length - 1];
    if (!freshestProj || !latestHist) return;

    const records = [];
    filteredRows.forEach(r => {
      visibleChannels.forEach(c => {
        const projected = Math.round(projectionValue(freshestProj, r.sku, c.value));
        const sales     = Math.round(lookupSales(r.sku, monthKey(latestHist), c.value));
        if (projected === 0 && sales === 0) return;
        records.push({
          date:            today,
          channel:         c.value,
          sku:             r.sku,
          asin:            r.asin || '',
          brand:           r.brand || '',
          projected_units: projected,
          sales_units:     sales,
          source:          'rolling',
          source_month:    freshestProj.month,
          source_year:     freshestProj.year,
        });
      });
    });
    pushWarehouseRecords(records);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, filteredRows, visibleChannels, month, year]);


  const buildSnapshotPayload = () => ({
    projection_month: month,
    projection_year:  year,
    rows: filteredRows.map(r => ({
      asin: r.asin || '',
      brand: r.brand || '',
      sku: r.sku,
      history: months.map(m => {
        const mKey = monthKey(m);
        const channels = {};
        visibleChannels.forEach(c => {
          channels[c.value] = Math.round(lookupSales(r.sku, mKey, c.value));
        });
        return { month: m.month, year: m.year, monthName: monthLabel(m.month, m.year), channels };
      }),
      projections: projectionMonths.map(pm => {
        const channels = {};
        const growthPctByChannel = {};
        visibleChannels.forEach(c => {
          channels[c.value] = Math.round(projectionValue(pm, r.sku, c.value));
          growthPctByChannel[c.value] = pctFor(pm, c.value);
        });
        return {
          month: pm.month, year: pm.year, monthName: monthLabel(pm.month, pm.year),
          channels, growthPctByChannel,
        };
      }),
    })),
  });

  const handleSaveSnapshot = async ({ silent = false } = {}) => {
    if (filteredRows.length === 0) {
      if (!silent) toast.error('No rows to snapshot');
      return;
    }
    setSavingSnapshot(true);
    try {
      const res = await axios.post(
        `${API}/rolling-projections/snapshots`,
        buildSnapshotPayload(),
        { headers: authHeaders() },
      );
      const v = res.data?.snapshot?.version;
      if (!silent) {
        toast.success(v ? `Snapshot saved · v${v}` : 'Historical snapshot saved');
      }
      // Refresh the list in the background so the dialog (if open) reflects it.
      fetchSnapshotsList();
    } catch (e) {
      // 409 = identical hash → server-side duplicate detection. In auto-save
      // mode we stay silent (this is the expected no-op when nothing changed).
      if (e.response?.status === 409) {
        if (!silent) toast.info('Snapshot already exists.');
      } else if (!silent) {
        toast.error(e.response?.data?.detail || 'Failed to save snapshot');
      }
    } finally {
      setSavingSnapshot(false);
    }
  };

  // Auto-save: debounce 2 s after any change in projection values / growth %
  // / Projection-Updated flags. The first run after mount is suppressed so we
  // don't snapshot on initial state load. Server-side dedup handles the case
  // where the user types-and-deletes back to the original value.
  const autoSaveTimerRef = useRef(null);
  const autoSaveSeededRef = useRef(false);
  useEffect(() => {
    if (!autoSaveEnabled) return undefined;
    if (!autoSaveSeededRef.current) {
      // Seed on first run so we don't fire immediately when the page first
      // loads existing edits/pct/flags from MongoDB.
      autoSaveSeededRef.current = true;
      return undefined;
    }
    if (loading || filteredRows.length === 0) return undefined;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      handleSaveSnapshot({ silent: true });
    }, 2000);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
    // We intentionally exclude handleSaveSnapshot from deps — it's a fresh
    // closure each render and would loop. Snapshot uses `filteredRows`,
    // `editsByMonth`, etc. directly when fired, which is correct.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSaveEnabled, editsByMonth, pctByMonth, updatedByMonth]);

  const fetchSnapshotsList = async () => {
    setSnapshotsLoading(true);
    try {
      const res = await axios.get(`${API}/rolling-projections/snapshots`,
        { headers: authHeaders() });
      setSnapshotsList(res.data.snapshots || []);
    } catch (e) {
      toast.error('Failed to load snapshots');
    } finally {
      setSnapshotsLoading(false);
    }
  };

  const handleOpenSnapshotsDialog = () => {
    setSnapshotsDialogOpen(true);
    fetchSnapshotsList();
  };

  const handleViewSnapshot = async (id) => {
    try {
      const res = await axios.get(`${API}/rolling-projections/snapshots/${id}`,
        { headers: authHeaders() });
      setViewingSnapshot(res.data);
    } catch (e) {
      toast.error('Failed to load snapshot');
    }
  };

  // Reuse the live Excel export style on a frozen snapshot. We iterate over
  // whatever the snapshot's first row exposes (channels list & projection
  // months) so the file always matches what the snapshot actually stores —
  // useful when the visible-channel filter at save time differed from now.
  const handleDownloadSnapshot = (snap) => {
    if (!snap?.rows?.length) { toast.error('Snapshot has no rows'); return; }
    const r0 = snap.rows[0];
    const channelKeys = r0.history?.[0]?.channels ? Object.keys(r0.history[0].channels) : [];
    const histMonths = r0.history || [];
    const projMonths = r0.projections || [];

    const groupRow = ['', '', ''];
    histMonths.forEach(m => {
      groupRow.push(m.monthName);
      for (let i = 1; i < channelKeys.length; i += 1) groupRow.push('');
    });
    projMonths.forEach(pm => {
      groupRow.push(`${pm.monthName} Projection`);
      for (let i = 1; i < channelKeys.length; i += 1) groupRow.push('');
    });
    const subRow = ['ASIN', 'Brand', 'SKU Name'];
    histMonths.forEach(() => channelKeys.forEach(ch => subRow.push(ch)));
    projMonths.forEach(() => channelKeys.forEach(ch => subRow.push(ch)));

    const aoa = [groupRow, subRow];
    snap.rows.forEach(r => {
      const row = [r.asin || '', r.brand || '', r.sku];
      r.history.forEach(m => channelKeys.forEach(ch => row.push(m.channels[ch] || 0)));
      r.projections.forEach(pm => channelKeys.forEach(ch => row.push(pm.channels[ch] || 0)));
      aoa.push(row);
    });

    // Growth % sheet
    const gAoa = [['SKU', ...projMonths.flatMap(pm => channelKeys.map(ch => `${pm.monthName} · ${ch} Growth%`))]];
    snap.rows.forEach(r => {
      const row = [r.sku];
      r.projections.forEach(pm => channelKeys.forEach(ch => {
        row.push(pm.growthPctByChannel?.[ch] ?? '');
      }));
      gAoa.push(row);
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const merges = [];
    for (let c = 0; c < 3; c += 1) merges.push({ s: { r: 0, c }, e: { r: 1, c } });
    let cursor = 3;
    histMonths.forEach(() => {
      merges.push({ s: { r: 0, c: cursor }, e: { r: 0, c: cursor + channelKeys.length - 1 } });
      cursor += channelKeys.length;
    });
    projMonths.forEach(() => {
      merges.push({ s: { r: 0, c: cursor }, e: { r: 0, c: cursor + channelKeys.length - 1 } });
      cursor += channelKeys.length;
    });
    ws['!merges'] = merges;
    ws['!cols'] = [{ wch: 14 }, { wch: 14 }, { wch: 36 },
      ...Array((histMonths.length + projMonths.length) * channelKeys.length).fill({ wch: 12 })];
    XLSX.utils.book_append_sheet(wb, ws, 'Rolling Projections');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(gAoa), 'Growth %');

    // Metadata sheet
    const meta = [
      ['Projection Month', MONTHS[snap.projection_month]],
      ['Projection Year', snap.projection_year],
      ['Version', snap.version ?? 1],
      ['Saved At', snap.save_timestamp],
      ['Saved By (user_id)', snap.user_id || '—'],
      ['Rows', snap.row_count],
      ['Note', snap.note || ''],
    ];
    const wsMeta = XLSX.utils.aoa_to_sheet(meta);
    wsMeta['!cols'] = [{ wch: 20 }, { wch: 40 }];
    XLSX.utils.book_append_sheet(wb, wsMeta, 'Snapshot Info');

    XLSX.writeFile(wb, `RollingProjections_Snapshot_${snap.save_date}_${MONTHS[snap.projection_month]}${snap.projection_year}${snap.version != null ? `_v${snap.version}` : ''}.xlsx`);
    toast.success('Snapshot downloaded');
  };

  // Admin-gated delete. Uses the same EDITOR_PASSWORD as Allocation Planner.
  const handleAttemptDelete = (id) => {
    if (!adminUnlocked) {
      setPendingDeleteId(id);
      setAdminPwDialogOpen(true);
      return;
    }
    runDelete(id, adminPwInMem);
  };

  const runDelete = async (id, password) => {
    try {
      await axios.delete(`${API}/rolling-projections/snapshots/${id}`, {
        headers: { ...authHeaders(), 'X-Editor-Password': password },
      });
      toast.success('Snapshot deleted');
      // Optimistically prune from the list and close the view modal if showing it.
      setSnapshotsList(prev => prev.filter(s => s.id !== id));
      if (viewingSnapshot?.id === id) setViewingSnapshot(null);
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem('editor_unlocked');
        sessionStorage.removeItem('editor_pw');
        setAdminUnlocked(false);
        setAdminPwInMem('');
        toast.error('Admin password expired — please re-enter');
        setAdminPwDialogOpen(true);
      } else {
        toast.error(e.response?.data?.detail || 'Failed to delete snapshot');
      }
    }
  };

  const handleAdminUnlockSubmit = async () => {
    if (!adminPwInput.trim()) return;
    try {
      await axios.post(`${API}/auth/editor-unlock`,
        { password: adminPwInput }, { headers: authHeaders() });
      sessionStorage.setItem('editor_unlocked', '1');
      sessionStorage.setItem('editor_pw', adminPwInput);
      setAdminUnlocked(true);
      setAdminPwInMem(adminPwInput);
      setAdminPwDialogOpen(false);
      const idToDelete = pendingDeleteId;
      const pw = adminPwInput;
      setAdminPwInput('');
      setPendingDeleteId(null);
      if (idToDelete) runDelete(idToDelete, pw);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid admin password');
    }
  };

  // Total table column count for the "no rows" colspan: 3 base + (2 sales + 3 proj) groups × channels + delete col.
  const numCols = 3 + (months.length + projectionMonths.length) * visibleChannels.length + 1;

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="max-w-[1900px] mx-auto p-4 md:p-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold tracking-tight text-wv-ink">
              Monthly Rolling Projections
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              Source: May'26 Projection (SKU master) · MTD Sales 1 · Auto = run-rate full-month × channel %
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs text-wv-body cursor-pointer select-none px-2 py-1 rounded border border-border hover:border-primary/60 transition-colors"
              data-testid="rolling-autosave-label">
              <input
                type="checkbox"
                checked={autoSaveEnabled}
                onChange={(e) => setAutoSaveEnabled(e.target.checked)}
                className="accent-primary w-3.5 h-3.5 cursor-pointer"
                data-testid="rolling-autosave-checkbox"
              />
              Auto Save Historical Snapshot
            </label>
            <Button
              variant="outline"
              onClick={() => handleSaveSnapshot()}
              disabled={loading || filteredRows.length === 0 || savingSnapshot}
              data-testid="rolling-save-snapshot-btn"
            >
              <Archive className="mr-2" size={16} />
              {savingSnapshot ? 'Saving…' : 'Save Historical Data'}
            </Button>
            <Button
              variant="outline"
              onClick={handleOpenSnapshotsDialog}
              data-testid="rolling-open-snapshots-btn"
            >
              <Eye className="mr-2" size={16} />
              Snapshots
            </Button>
            <Button
              variant="outline"
              onClick={handleDownloadExcel}
              disabled={loading}
              data-testid="rolling-download-excel"
              title={filteredRows.length === 0 ? 'No rows match the current filters' : 'Download the visible table as XLSX'}
            >
              <Download className="mr-2" size={16} />
              Download Excel
            </Button>
            <Button onClick={fetchSheets} disabled={loading} data-testid="rolling-refresh-button">
              <RefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} size={16} />
              {loading ? 'Refreshing…' : 'Refresh'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1900px] mx-auto p-4 md:p-8 space-y-6">
        {/* Top controls */}
        <section className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end" data-testid="rolling-controls">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block">
              Month
            </Label>
            <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
              <SelectTrigger data-testid="rolling-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => (
                  <SelectItem key={m} value={String(i)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block">
              Year
            </Label>
            <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
              <SelectTrigger data-testid="rolling-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {yearOptions.map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block">
              Brand Filter
            </Label>
            <MultiSelect
              testId="rolling-brand-filter"
              options={brandOptions}
              selected={selectedBrands}
              onChange={setSelectedBrands}
              placeholder="All Brands"
              emptyLabel="All Brands"
              searchable
            />
          </div>
        </section>

        {/* Channel chips */}
        <section data-testid="rolling-channel-chips" className="flex flex-wrap gap-2">
          {[{ value: ALL, label: 'All' }, ...CHANNELS.map(c => ({ value: c.value, label: c.value }))].map(chip => {
            const active = channelFilter === chip.value;
            return (
              <button
                key={chip.value}
                type="button"
                onClick={() => setChannelFilter(chip.value)}
                data-testid={`rolling-channel-${chip.value === ALL ? 'all' : chip.value.toLowerCase().replace(/\s+/g, '-').replace(/\./g, '')}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  active
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-card text-wv-body border-border hover:bg-muted'
                }`}
              >
                {chip.label}
              </button>
            );
          })}
        </section>

        {/* Summary cards */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card data-testid="kpi-last-2-months">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Last 2 Months Total Sales Units
                </p>
                <ShoppingBag size={18} className="text-primary" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-semibold tracking-tight text-wv-ink tabular-nums">
                {formatInt(totalLast2MonthsSales)}
              </p>
              {/* Month-wise split */}
              <div className="mt-3 grid grid-cols-2 gap-3" data-testid="kpi-last-2-months-split">
                {months.map(m => {
                  const mKey = `${m.year}-${m.month}`;
                  return (
                    <div key={mKey} className="rounded-md bg-muted/40 px-3 py-2">
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                        {monthLabel(m.month, m.year)}
                      </p>
                      <p
                        className="text-lg font-semibold tabular-nums text-wv-ink mt-0.5"
                        data-testid={`kpi-month-${mKey}`}
                      >
                        {formatInt(perMonthSalesTotal[mKey] || 0)}
                      </p>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                {channelFilter === ALL ? 'All channels' : channelFilter}
                {selectedBrands.length > 0 ? ` · ${selectedBrands.length} brand${selectedBrands.length === 1 ? '' : 's'}` : ''}
              </p>
            </CardContent>
          </Card>

          <Card data-testid="kpi-selected-projected">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  3-Month Total Projected Units
                </p>
                <Target size={18} className="text-primary" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-semibold tracking-tight text-wv-ink tabular-nums">
                {formatInt(totalProjected)}
              </p>
              <div className="mt-3 grid grid-cols-3 gap-2" data-testid="kpi-projection-split">
                {projectionMonths.map(pm => {
                  const pKey = monthKey(pm);
                  return (
                    <div key={pKey} className="rounded-md bg-emerald-50 px-2 py-2 border border-emerald-100">
                      <p className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">
                        {monthLabel(pm.month, pm.year)}
                      </p>
                      <p
                        className="text-lg font-semibold tabular-nums text-emerald-900 mt-0.5"
                        data-testid={`kpi-proj-${pKey}`}
                      >
                        {formatInt(perMonthProjectionTotal[pKey] || 0)}
                      </p>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Run-rate × per-channel % of {monthLabel(baseMonth.month, baseMonth.year)} sales · edits & formulas override
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Summary line */}
        <p className="text-xs text-muted-foreground" data-testid="rolling-summary-text">
          Showing <span className="font-semibold text-wv-ink">{filteredRows.length}</span> SKUs ·
          {' '}Sales for <span className="font-semibold text-wv-ink">{monthLabel(months[0].month, months[0].year)}</span>
          {' '}and <span className="font-semibold text-wv-ink">{monthLabel(months[1].month, months[1].year)}</span>
          {' · '}Projecting <span className="font-semibold text-wv-ink">{monthLabel(projectionMonths[0].month, projectionMonths[0].year)}</span>,
          {' '}<span className="font-semibold text-wv-ink">{monthLabel(projectionMonths[1].month, projectionMonths[1].year)}</span>,
          {' '}<span className="font-semibold text-wv-ink">{monthLabel(projectionMonths[2].month, projectionMonths[2].year)}</span>
        </p>

        {/* Table */}
        <Card className="overflow-hidden" data-testid="rolling-table-card">
          <div className="overflow-auto" style={{ maxHeight: '70vh' }}>
            <table className="w-full border-collapse text-sm" data-testid="rolling-table">
              <thead className="sticky top-0 z-30">
                <tr className="bg-primary text-primary-foreground">
                  <th rowSpan={3} className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-widest border-r border-primary/40 bg-primary sticky left-0 z-40" style={{ width: 140, minWidth: 140, maxWidth: 140 }}>
                    ASIN
                  </th>
                  <th rowSpan={3} className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-widest border-r border-primary/40 bg-primary sticky z-40" style={{ left: 140, width: 130, minWidth: 130, maxWidth: 130 }}>
                    Brand
                  </th>
                  <th rowSpan={3} className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-widest border-r-2 border-primary-foreground/30 bg-primary sticky z-40" style={{ left: 270, width: 320, minWidth: 320, maxWidth: 320 }}>
                    SKU Name
                  </th>
                  {months.map((m) => (
                    <th
                      key={`mh-${m.year}-${m.month}`}
                      colSpan={visibleChannels.length}
                      className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-widest border-r border-primary/40 bg-primary"
                    >
                      {monthLabel(m.month, m.year)}
                    </th>
                  ))}
                  {projectionMonths.map((pm, pi) => (
                    <th
                      key={`pmh-${pm.year}-${pm.month}`}
                      colSpan={visibleChannels.length}
                      className={`px-3 py-2 text-center text-xs font-semibold uppercase tracking-widest bg-emerald-700 ${pi < projectionMonths.length - 1 ? 'border-r border-emerald-900' : ''}`}
                      data-testid={`rolling-projection-header-${monthKey(pm)}`}
                    >
                      {monthLabel(pm.month, pm.year)} Auto Projection
                    </th>
                  ))}
                  <th rowSpan={3} className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-widest bg-primary">
                    &nbsp;
                  </th>
                </tr>
                <tr className="bg-primary/90 text-primary-foreground">
                  {months.map((m) => visibleChannels.map((c, ci) => (
                    <th
                      key={`ch-${m.year}-${m.month}-${c.value}`}
                      className={`px-3 py-2 text-right text-[11px] font-semibold tracking-wide ${
                        ci === visibleChannels.length - 1 ? 'border-r border-primary/40' : ''
                      }`}
                    >
                      {c.value}
                    </th>
                  )))}
                  {projectionMonths.map((pm, pi) => visibleChannels.map((c, ci) => {
                    const mk = monthKey(pm);
                    const flagged = !!updatedByMonth[mk]?.[c.value];
                    return (
                      <th
                        key={`pch-${pm.year}-${pm.month}-${c.value}`}
                        className={`px-3 py-2 text-right text-[11px] font-semibold tracking-wide bg-emerald-700 ${pi < projectionMonths.length - 1 && ci === visibleChannels.length - 1 ? 'border-r border-emerald-900' : ''}`}
                      >
                        <div className="flex items-center justify-end gap-1.5">
                          <span>{c.value}</span>
                          <button
                            type="button"
                            onClick={() => handleToggleUpdated(pm, c.value)}
                            data-testid={`rolling-updated-${mk}-${c.value}`}
                            className={`inline-flex items-center justify-center w-4 h-4 rounded transition-colors ${flagged ? 'bg-amber-300 text-emerald-900 hover:bg-amber-400' : 'bg-emerald-900/50 text-emerald-200 hover:bg-emerald-900'}`}
                            title={flagged ? `Marked "Projection Updated" — click to unmark` : `Click to mark "Projection Updated" for ${c.value} (${monthLabel(pm.month, pm.year)})`}
                          >
                            <Check size={10} strokeWidth={3} />
                          </button>
                        </div>
                      </th>
                    );
                  }))}
                </tr>
                {/* % controls row — one input per (projection month, channel). Sales
                    columns get an em-dash spacer. Editing a % regenerates that
                    month's column for that channel (clears edits). */}
                <tr className="bg-emerald-50 text-emerald-900">
                  {months.map((m) => visibleChannels.map((c, ci) => (
                    <th
                      key={`ph-${m.year}-${m.month}-${c.value}`}
                      className={`px-2 py-1 text-right text-[10px] font-normal text-muted-foreground ${
                        ci === visibleChannels.length - 1 ? 'border-r border-primary/40' : ''
                      }`}
                    >
                      —
                    </th>
                  )))}
                  {projectionMonths.map((pm, pi) => visibleChannels.map((c, ci) => {
                    const cur = pctByMonth[monthKey(pm)]?.[c.value];
                    return (
                      <th
                        key={`pc-${pm.year}-${pm.month}-${c.value}`}
                        className={`px-1 py-1 text-right text-[11px] font-medium bg-emerald-50 ${pi < projectionMonths.length - 1 && ci === visibleChannels.length - 1 ? 'border-r border-emerald-300' : ''}`}
                      >
                        <div className="flex items-center justify-end gap-0.5">
                          <input
                            type="number"
                            step="any"
                            value={cur ?? ''}
                            placeholder={`+${DEFAULT_PCT}`}
                            onChange={(e) => handlePctChange(pm, c.value, e.target.value)}
                            data-testid={`rolling-pct-${monthKey(pm)}-${c.value}`}
                            className="w-14 text-right bg-white border border-emerald-300 rounded px-1.5 py-0.5 text-emerald-900 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                            title={`Growth % for ${c.value} ${monthLabel(pm.month, pm.year)} — 20 = +20% (default), -10 = -10%. Changing regenerates the column.`}
                          />
                          <span className="text-[10px] text-emerald-700">%</span>
                        </div>
                      </th>
                    );
                  }))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 && (
                  <tr>
                    <td colSpan={numCols} className="text-center py-8 text-muted-foreground">
                      No SKUs match the selected filters
                    </td>
                  </tr>
                )}
                {filteredRows.map((r, ri) => {
                  // Zebra striping — sticky cells MUST use a fully opaque bg so
                  // they don't let the scrolling cells bleed through.
                  const rowBg = ri % 2 === 0 ? 'bg-white' : 'bg-slate-50';
                  return (
                  <tr
                    key={`${r.source}-${r.id || r.sku}-${ri}`}
                    className={rowBg}
                    data-testid={`rolling-row-${ri}`}
                  >
                    <td className={`px-3 py-2 text-xs text-wv-ink whitespace-nowrap sticky left-0 z-10 ${rowBg}`} style={{ width: 140, minWidth: 140, maxWidth: 140 }}>{r.asin || '—'}</td>
                    <td className={`px-3 py-2 text-xs text-wv-body whitespace-nowrap sticky z-10 ${rowBg}`} style={{ left: 140, width: 130, minWidth: 130, maxWidth: 130 }}>{r.brand || '—'}</td>
                    <td className={`px-3 py-2 text-xs text-wv-ink sticky z-10 border-r-2 border-border ${rowBg}`} style={{ left: 270, width: 320, minWidth: 320, maxWidth: 320 }}>
                      <div className="flex items-center gap-2">
                        <span className="truncate">{r.sku}</span>
                        {r.source === 'custom' && (
                          <span className="text-[10px] uppercase tracking-wider bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                            custom
                          </span>
                        )}
                      </div>
                    </td>
                    {months.map((m, mi) => {
                      const mKey = `${m.year}-${m.month}`;
                      return visibleChannels.map((c, ci) => {
                        const colIdx = mi * visibleChannels.length + ci;
                        const isBaseMonth = mKey === monthKey(baseMonth);
                        return (
                          <td
                            key={`s-${mKey}-${c.value}-${ri}`}
                            className={`px-3 py-2 text-right text-xs tabular-nums cursor-cell ${isBaseMonth ? 'text-emerald-800 italic' : 'text-wv-body'}`}
                            title={isBaseMonth ? `Projected full-${MONTHS[baseMonth.month]} value` : `${colLetters(colIdx)}${ri + 1}`}
                            onClick={() => insertA1Ref(`${colLetters(colIdx)}${ri + 1}`)}
                            data-testid={`rolling-sales-${ri}-${colIdx}`}
                          >
                            {formatInt(lookupSales(r.sku, mKey, c.value))}
                          </td>
                        );
                      });
                    })}
                    {projectionMonths.map((pm, pi) => visibleChannels.map((c, ci) => {
                      const ek = `${norm(r.sku)}__${c.value}`;
                      const raw = editsByMonth[monthKey(pm)]?.[ek];
                      const edited = raw != null && raw !== '';
                      const isFormula = edited && typeof raw === 'string' && raw.trim().startsWith('=');
                      const focusKey = `${monthKey(pm)}__${ri}__${c.value}`;
                      const isFocused = focusedCell && focusedCell.key === focusKey;
                      // A1 col index — after the sales-month groups.
                      const colIdx = months.length * visibleChannels.length
                        + pi * visibleChannels.length + ci;
                      const displayValue = (() => {
                        if (isFocused) return edited ? String(raw) : '';
                        if (!edited) return formatInt(projectionValue(pm, r.sku, c.value));
                        if (isFormula) return formatInt(projectionValue(pm, r.sku, c.value));
                        return formatInt(Number((raw || '').toString().replace(/,/g, '')) || 0);
                      })();
                      const tone = editTone(pm, r.sku, c.value);
                      const cellTitle = isFormula
                        ? `Formula: ${raw}  →  ${formatInt(projectionValue(pm, r.sku, c.value))}`
                        : `${colLetters(colIdx)}${ri + 1}`;
                      const groupBorder = pi < projectionMonths.length - 1 && c === visibleChannels[visibleChannels.length - 1];
                      return (
                        <td
                          key={`p-${monthKey(pm)}-${c.value}-${ri}`}
                          className={`px-1 py-1 text-right text-xs tabular-nums ${tone.cell} ${groupBorder ? 'border-r border-emerald-300' : ''}`}
                          title={cellTitle}
                          onClick={() => {
                            // Click while another cell is in formula-editing mode →
                            // insert a ref to THIS cell into that formula.
                            insertA1Ref(`${colLetters(colIdx)}${ri + 1}`);
                          }}
                        >
                          <input
                            type="text"
                            value={displayValue}
                            onChange={(e) => handleProjectionEdit(pm, r.sku, c.value, e.target.value)}
                            onKeyDown={(e) => handleCellKeyDown(pm, r.sku, c.value, ri, colIdx, e)}
                            onFocus={(e) => {
                              setFocusedCell({ key: focusKey, target: pm, skuName: r.sku, channel: c.value, rowIdx: ri, colIdx });
                              focusedInputRef.current = e.target;
                            }}
                            onBlur={() => {
                              setFocusedCell((cur) => (cur && cur.key === focusKey ? null : cur));
                            }}
                            data-testid={`rolling-proj-${monthKey(pm)}-${ri}-${c.value}`}
                            placeholder="auto"
                            className={`w-full text-right bg-transparent border ${tone.input} rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-500 ${isFormula && isFocused ? 'font-mono text-[11px]' : ''}`}
                          />
                        </td>
                      );
                    }))}
                    <td className="px-3 py-2 text-center">
                      {r.source === 'custom' && (
                        <button
                          type="button"
                          onClick={() => handleDeleteSku(r.id)}
                          data-testid={`rolling-delete-${r.id}`}
                          className="text-muted-foreground hover:text-red-600 transition-colors"
                          title="Remove custom SKU"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
              {filteredRows.length > 0 && (
                <tfoot className="sticky bottom-0 z-20">
                  <tr className="bg-wv-ink text-white font-semibold">
                    <td className="px-3 py-2 text-xs uppercase tracking-widest sticky left-0 z-30 bg-wv-ink" style={{ width: 140, minWidth: 140, maxWidth: 140 }}>
                      Total
                    </td>
                    <td className="bg-wv-ink sticky z-30" style={{ left: 140, width: 130, minWidth: 130, maxWidth: 130 }}>&nbsp;</td>
                    <td className="bg-wv-ink sticky z-30 border-r-2 border-white/20" style={{ left: 270, width: 320, minWidth: 320, maxWidth: 320 }}>&nbsp;</td>
                    {months.map(m => {
                      const mKey = `${m.year}-${m.month}`;
                      return visibleChannels.map(c => (
                        <td
                          key={`t-${mKey}-${c.value}`}
                          className="px-3 py-2 text-right text-xs tabular-nums"
                          data-testid={`rolling-total-${mKey}-${c.value}`}
                        >
                          {formatInt(totals[`${mKey}__${c.value}`])}
                        </td>
                      ));
                    })}
                    {projectionMonths.map((pm, pi) => visibleChannels.map((c, ci) => (
                      <td
                        key={`tp-${monthKey(pm)}-${c.value}`}
                        className={`px-3 py-2 text-right text-xs tabular-nums bg-emerald-900 ${pi < projectionMonths.length - 1 && ci === visibleChannels.length - 1 ? 'border-r border-emerald-700' : ''}`}
                        data-testid={`rolling-total-proj-${monthKey(pm)}-${c.value}`}
                      >
                        {formatInt(totals[`proj-${monthKey(pm)}__${c.value}`])}
                      </td>
                    )))}
                    <td>&nbsp;</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <div className="flex items-center justify-between p-3 border-t border-border bg-muted/40">
            <p className="text-xs text-muted-foreground">
              {addedSkus.length} custom SKU{addedSkus.length === 1 ? '' : 's'} added · {projectionRows.length > 1 ? projectionRows.length - 1 : 0} base SKUs from sheet
              {(() => {
                const editCount = Object.values(editsByMonth).reduce((s, m) => s + Object.keys(m).length, 0);
                return editCount > 0 ? ` · ${editCount} cell${editCount === 1 ? '' : 's'} edited across ${projectionMonths.length} projection months` : '';
              })()}
            </p>
            <Button
              variant="default"
              onClick={() => setDialogOpen(true)}
              data-testid="rolling-add-sku-button"
              size="sm"
            >
              <Plus size={14} className="mr-1" />
              Add SKU
            </Button>
          </div>
        </Card>
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="rolling-add-sku-dialog">
          <DialogHeader>
            <DialogTitle>Add Custom SKU</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1.5 block">
                ASIN <span className="text-red-500">*</span>
              </Label>
              <Input
                value={newAsin}
                onChange={(e) => setNewAsin(e.target.value)}
                placeholder="e.g. B0XXXXXXX"
                data-testid="rolling-input-asin"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1.5 block">
                Brand <span className="text-red-500">*</span>
              </Label>
              <Input
                value={newBrand}
                onChange={(e) => setNewBrand(e.target.value)}
                placeholder="e.g. Wellcore"
                data-testid="rolling-input-brand"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1.5 block">
                SKU Name <span className="text-red-500">*</span>
              </Label>
              <Input
                value={newSku}
                onChange={(e) => setNewSku(e.target.value)}
                placeholder="e.g. Creatine New Flavour (100g)"
                data-testid="rolling-input-sku"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={saving}
              data-testid="rolling-cancel-add"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddSku}
              disabled={saving}
              data-testid="rolling-confirm-add"
            >
              {saving ? 'Adding…' : 'Add SKU'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ Historical Snapshots dialog ============
          Lists every saved snapshot newest-first. Each row exposes View,
          Download (Excel) and Delete (admin-gated) actions. Loading the list
          is lightweight (server strips `rows` from the projection so we don't
          ship the entire grid for the list view). */}
      <Dialog open={snapshotsDialogOpen} onOpenChange={setSnapshotsDialogOpen}>
        <DialogContent className="max-w-4xl" data-testid="rolling-snapshots-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive size={18} /> Historical Snapshots
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-auto max-h-[60vh]">
            {snapshotsLoading ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
            ) : snapshotsList.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center" data-testid="snapshots-empty">
                No snapshots yet. Click <strong>Save Historical Data</strong> to capture the current view.
              </p>
            ) : (
              <table className="w-full border-collapse text-sm" data-testid="snapshots-table">
                <thead className="bg-primary text-primary-foreground sticky top-0">
                  <tr>
                    <th className="text-left p-2 text-xs uppercase tracking-widest">Projection</th>
                    <th className="text-left p-2 text-xs uppercase tracking-widest">Saved At</th>
                    <th className="text-right p-2 text-xs uppercase tracking-widest">SKUs</th>
                    <th className="text-right p-2 text-xs uppercase tracking-widest">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {snapshotsList.map((s, i) => (
                    <tr key={s.id} className={`${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'} border-b border-border`} data-testid={`snapshot-row-${s.id}`}>
                      <td className="p-2 text-xs text-wv-ink">
                        <span className="font-medium">{MONTHS[s.projection_month]} {s.projection_year}</span>
                        {s.version != null && (
                          <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-primary/10 text-primary"
                            data-testid={`snapshot-version-${s.id}`}>
                            v{s.version}
                          </span>
                        )}
                      </td>
                      <td className="p-2 text-xs text-wv-body">
                        {new Date(s.save_timestamp).toLocaleString()}
                      </td>
                      <td className="p-2 text-xs text-right tabular-nums">{s.row_count}</td>
                      <td className="p-2 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <Button size="sm" variant="outline" className="h-7 px-2"
                            onClick={() => handleViewSnapshot(s.id)}
                            data-testid={`snapshot-view-${s.id}`}
                          >
                            <Eye size={12} className="mr-1" />View
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 px-2"
                            onClick={async () => {
                              const r = await axios.get(`${API}/rolling-projections/snapshots/${s.id}`, { headers: authHeaders() });
                              handleDownloadSnapshot(r.data);
                            }}
                            data-testid={`snapshot-download-${s.id}`}
                          >
                            <Download size={12} className="mr-1" />Excel
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 px-2 border-rose-500 text-rose-600 hover:bg-rose-50"
                            onClick={() => handleAttemptDelete(s.id)}
                            data-testid={`snapshot-delete-${s.id}`}
                            title="Admin only"
                          >
                            <Trash2 size={12} className="mr-1" />Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSnapshotsDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Snapshot detail viewer — read-only, exact-as-saved. */}
      <Dialog open={!!viewingSnapshot} onOpenChange={(o) => { if (!o) setViewingSnapshot(null); }}>
        <DialogContent className="max-w-6xl" data-testid="snapshot-viewer">
          <DialogHeader>
            <DialogTitle>
              {viewingSnapshot
                ? `Snapshot · ${MONTHS[viewingSnapshot.projection_month]} ${viewingSnapshot.projection_year}${viewingSnapshot.version != null ? ` · v${viewingSnapshot.version}` : ''}`
                : 'Snapshot'}
            </DialogTitle>
          </DialogHeader>
          {viewingSnapshot && (
            <>
              <div className="text-xs text-muted-foreground mb-2">
                Saved {new Date(viewingSnapshot.save_timestamp).toLocaleString()} · {viewingSnapshot.row_count} SKUs · User <code className="text-[10px] bg-slate-100 px-1 rounded">{viewingSnapshot.user_id}</code>
              </div>
              <div className="overflow-auto max-h-[65vh] border border-border rounded">
                <table className="w-full border-collapse text-xs" data-testid="snapshot-detail-table">
                  <thead className="bg-primary text-primary-foreground sticky top-0">
                    {(() => {
                      const r0 = viewingSnapshot.rows?.[0];
                      const channelKeys = r0?.history?.[0]?.channels ? Object.keys(r0.history[0].channels) : [];
                      const histMonths = r0?.history || [];
                      const projMonths = r0?.projections || [];
                      return (
                        <>
                          <tr>
                            <th rowSpan={2} className="p-2 text-left uppercase tracking-widest text-[10px]">ASIN</th>
                            <th rowSpan={2} className="p-2 text-left uppercase tracking-widest text-[10px]">Brand</th>
                            <th rowSpan={2} className="p-2 text-left uppercase tracking-widest text-[10px]">SKU</th>
                            {histMonths.map(m => (
                              <th key={`h-${m.monthName}`} colSpan={channelKeys.length} className="p-2 text-center uppercase tracking-widest text-[10px] border-l border-white/20">
                                {m.monthName}
                              </th>
                            ))}
                            {projMonths.map(pm => (
                              <th key={`p-${pm.monthName}`} colSpan={channelKeys.length} className="p-2 text-center uppercase tracking-widest text-[10px] border-l border-white/20 bg-emerald-700">
                                {pm.monthName} Projection
                              </th>
                            ))}
                          </tr>
                          <tr>
                            {histMonths.map((m) => channelKeys.map(ch => (
                              <th key={`h-${m.monthName}-${ch}`} className="p-2 text-right text-[10px] font-medium border-l border-white/10">{ch}</th>
                            )))}
                            {projMonths.map((pm) => channelKeys.map(ch => (
                              <th key={`p-${pm.monthName}-${ch}`} className="p-2 text-right text-[10px] font-medium border-l border-white/10 bg-emerald-700">{ch}</th>
                            )))}
                          </tr>
                        </>
                      );
                    })()}
                  </thead>
                  <tbody>
                    {viewingSnapshot.rows.map((r, ri) => (
                      <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                        <td className="p-2 text-wv-ink">{r.asin || '—'}</td>
                        <td className="p-2 text-wv-body">{r.brand || '—'}</td>
                        <td className="p-2 text-wv-ink">{r.sku}</td>
                        {r.history.flatMap(m => Object.entries(m.channels).map(([ch, v]) => (
                          <td key={`${ri}-h-${m.monthName}-${ch}`} className="p-2 text-right tabular-nums border-l border-border/40">{formatInt(v)}</td>
                        )))}
                        {r.projections.flatMap(pm => Object.entries(pm.channels).map(([ch, v]) => (
                          <td key={`${ri}-p-${pm.monthName}-${ch}`} className="p-2 text-right tabular-nums text-emerald-900 font-medium border-l border-border/40">{formatInt(v)}</td>
                        )))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => handleDownloadSnapshot(viewingSnapshot)} disabled={!viewingSnapshot}>
              <Download size={14} className="mr-2" />Download Excel
            </Button>
            <Button variant="outline" onClick={() => setViewingSnapshot(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin password gate for Delete. */}
      <Dialog open={adminPwDialogOpen} onOpenChange={(o) => { if (!o) { setAdminPwDialogOpen(false); setAdminPwInput(''); setPendingDeleteId(null); } }}>
        <DialogContent className="max-w-sm" data-testid="admin-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><LockKeyhole size={18} /> Admin Password Required</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Admin Password</Label>
            <Input
              type="password" autoFocus value={adminPwInput}
              onChange={(e) => setAdminPwInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAdminUnlockSubmit(); }}
              data-testid="admin-pw-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => { setAdminPwDialogOpen(false); setAdminPwInput(''); setPendingDeleteId(null); }}>Cancel</Button>
            <Button size="sm" onClick={handleAdminUnlockSubmit} disabled={!adminPwInput.trim()} data-testid="admin-pw-submit">Unlock & Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
